import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Table } from '@/components/ui/Table';
import { NewBacktestDialog } from '@/components/backtests/NewBacktestDialog';
import {
  formatDateTime,
  formatSigned,
  getBacktestStatusTone,
  getNetPnl,
  getReturnPercent,
  isCertifiedRun,
  isActiveRun,
} from '@/components/backtests/backtestPresentation';
import { useBacktests, useDatasets, useStrategies } from '@/api/queries';
import type { BacktestRunDto } from '@/api/types';
import styles from './BacktestsPage.module.css';

type StatusFilter = 'All' | 'Queued' | 'Running' | 'Succeeded' | 'Failed';

export default function BacktestsPage() {
  const { data: backtests, isLoading, error } = useBacktests({
    refetchInterval: (query) => query.state.data?.some(isActiveRun) ? 2_000 : false,
  });
  const { data: datasets } = useDatasets();
  const { data: strategies } = useStrategies();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('All');

  const datasetNames = useMemo(
    () => new Map((datasets ?? []).map((dataset) => [dataset.id, dataset.displayName ?? dataset.id])),
    [datasets],
  );
  const filteredRuns = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (backtests ?? []).filter((run) => {
      if (status !== 'All' && run.status !== status) return false;
      if (!query) return true;
      const datasetName = datasetNames.get(run.datasetId) ?? run.datasetId ?? '';
      return `${run.strategyName ?? ''} ${datasetName} ${run.id ?? ''}`.toLowerCase().includes(query);
    });
  }, [backtests, datasetNames, search, status]);

  return (
    <div className={styles.page}>
      <div className={styles.headingRow}>
        <div>
          <h1>Backtests</h1>
          <p>Queue strategy runs, monitor progress, and inspect results from one workspace.</p>
        </div>
        <Button type="button" variant="primary" onClick={() => setDialogOpen(true)}>
          <Plus size={16} /> New backtest
        </Button>
      </div>

      <Card>
        <div className={styles.filters}>
          <Input
            label="Search runs"
            placeholder="Strategy, dataset, or run ID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value as StatusFilter)}
            options={['All', 'Queued', 'Running', 'Succeeded', 'Failed'].map((value) => ({ value, label: value }))}
          />
          <span className={styles.resultCount}>{filteredRuns.length.toLocaleString()} runs</span>
        </div>

        {isLoading && <p className="statusText statusTextMuted">Loading backtests…</p>}
        {error && <p className="statusText statusTextDanger">{error.message}</p>}
        {!isLoading && !error && filteredRuns.length === 0 && (
          <div className={styles.emptyState}>
            <strong>{backtests?.length ? 'No runs match these filters.' : 'No backtests yet.'}</strong>
            <span>{backtests?.length ? 'Try a different search or status.' : 'Create a run to start evaluating a strategy.'}</span>
          </div>
        )}
        {filteredRuns.length > 0 && (
          <Table headers={['Strategy / Dataset', 'Status', 'Net P&L / Return', 'Completed trades', 'Max drawdown', 'Created', '']}>
            {filteredRuns.map((run) => (
              <RunRow
                key={run.id ?? `${run.datasetId}-${run.createdUtc}`}
                run={run}
                datasetName={datasetNames.get(run.datasetId)}
              />
            ))}
          </Table>
        )}
      </Card>

      {dialogOpen && (
        <NewBacktestDialog
          datasets={datasets ?? []}
          strategies={strategies ?? []}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </div>
  );
}

function RunRow({ run, datasetName }: { run: BacktestRunDto; datasetName?: string | null }) {
  const net = getNetPnl(run);
  const returnPercent = getReturnPercent(run);
  return (
    <tr>
      <td>
        <div className={styles.primaryCell}>
          <strong>{run.strategyName ?? 'Unnamed strategy'}</strong>
          <span>{datasetName ?? run.datasetId ?? 'Unknown dataset'}</span>
          {run.status === 'Succeeded' && !isCertifiedRun(run) && <small>Legacy metrics—rerun to verify</small>}
        </div>
      </td>
      <td>
        <Badge variant={getBacktestStatusTone(run.status)}>{run.status ?? 'Unknown'}</Badge>
        {isActiveRun(run) && <div className={styles.progressText}>{Math.round((run.progress ?? 0) * 100)}%</div>}
      </td>
      <td className={styles.mono}>
        {net == null ? '—' : <span className={net >= 0 ? styles.positive : styles.negative}>{formatSigned(net)}</span>}
        <small>{returnPercent == null ? '' : `${formatSigned(returnPercent)}%`}</small>
      </td>
      <td className={styles.mono}>{run.totalTrades ?? 0}</td>
      <td className={styles.mono}>
        {run.maxDrawdown == null ? '—' : run.maxDrawdown.toFixed(2)}
        <small>{run.maxDrawdownPercent == null ? '' : `${run.maxDrawdownPercent.toFixed(2)}%`}</small>
      </td>
      <td className={styles.mono}>{formatDateTime(run.createdUtc)}</td>
      <td>
        {run.id && (
          <Link to={`/backtests/${encodeURIComponent(run.id)}`}>
            <Button type="button" variant="outline-info"><Eye size={14} /> View</Button>
          </Link>
        )}
      </td>
    </tr>
  );
}
